import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ProfilePageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import api from '../../api/axios';
import {
  User,
  Mail,
  Key,
  Building,
  Briefcase,
  Calendar,
  Edit2,
  Save,
  X,
  Shield,
  Globe,
  CheckCircle,
  AlertCircle,
  Activity,
  Sparkles,
  Lock,
} from 'lucide-react';

/** Same values/labels as Register page department field */
const DEPARTMENT_OPTIONS = [
  { value: 'design_pm', label: 'Design & PM' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'fabrication', label: 'Fabrication' },
  { value: 'fitting_paint_test', label: 'Fitting, Paint & Test' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'impeller', label: 'Impeller' },
  { value: 'JBT', label: 'JBT' },
  { value: 'production', label: 'Production' },
  { value: 'service', label: 'Service' },
  { value: 'TSI', label: 'TSI' },
];

const UserRole = { User: 0, Employee: 1, Lead: 2, PM: 3, Admin: 4 };

const getRoleBadgeStyle = (roleCode) => {
  switch (roleCode) {
    case UserRole.Admin: return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
    case UserRole.PM: return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800';
    case UserRole.Lead: return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    case UserRole.Employee: return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
  }
};

/** Read-only or editable field; when not editable, stays text even in edit mode */
const InfoField = React.memo(({
  icon: Icon,
  label,
  value,
  name,
  editable = true,
  isEditing,
  fieldValue,
  onChange,
  accent,
  hint,
}) => {
  const { t } = useTranslation();
  const showInput = isEditing && editable;
  const display = value ?? fieldValue ?? '—';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 flex-shrink-0 opacity-80" style={{ color: accent }} />
        <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </label>
        {!editable && isEditing && (
          <Lock className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" title={t('pages.profile.cannotEditHint')} aria-hidden />
        )}
      </div>
      {showInput ? (
        <input
          type="text"
          name={name}
          value={fieldValue ?? ''}
          onChange={onChange}
          className="w-full px-3.5 py-2.5 bg-white/80 dark:bg-gray-800/80 border border-gray-200/90 dark:border-gray-600/80 rounded-xl focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50 text-gray-900 dark:text-gray-100 text-sm transition-all"
        />
      ) : (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed min-h-[1.25rem]">
          {display === '' || display == null ? '—' : display}
        </p>
      )}
      {hint ? (
        <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
});

const StatusRow = ({ label, children }) => (
  <div className="flex items-center justify-between py-2.5 gap-4">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    {children}
  </div>
);

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user: authUser, refreshCurrentUser } = useAuth();
  const { prefs } = useTheme();
  const accent = prefs?.accentColor || '#f59e0b';

  const getRoleName = (roleCode) => {
    switch (roleCode) {
      case UserRole.User: return t('pages.profile.roleUser');
      case UserRole.Employee: return t('pages.profile.roleEmployee');
      case UserRole.Lead: return t('pages.profile.roleLead');
      case UserRole.PM: return t('pages.profile.rolePm');
      case UserRole.Admin: return t('pages.profile.roleAdmin');
      default: return t('pages.profile.roleUser');
    }
  };

  const departmentLabel = (value) => {
    if (!value) return null;
    const o = DEPARTMENT_OPTIONS.find((x) => x.value === value);
    return t(`pages.profile.dept_${value}`, { defaultValue: o?.label ?? value });
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    department: '',
    language: 'en',
  });

  useEffect(() => {
    if (authUser?.id) {
      fetchUserProfile();
    }
  }, [authUser?.id]);

  const fetchUserProfile = async () => {
    if (!authUser?.id) return;
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/auth/${authUser.id}`);
      const body = response.data;
      const userData = body?.data ?? body;
      if (!userData?.id) {
        setError(t('pages.profile.invalidResponse'));
        setUser(null);
        return;
      }

      setUser(userData);
      setFormData({
        department: userData.department || '',
        language: userData.language || 'en',
      });
    } catch (err) {
      setError(err.response?.data?.message || t('pages.profile.loadFailed'));
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = async () => {
    if (!authUser?.id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        department: formData.department,
        language: formData.language,
      };

      const response = await api.put(`/api/auth/${authUser.id}`, payload);
      const body = response.data;

      if (body?.isSuccess === false) {
        setError(body?.message || t('pages.profile.updateFailed'));
        return;
      }

      const updated = body?.data;
      if (!updated?.id) {
        setError(t('pages.profile.incomplete'));
        await fetchUserProfile();
        await refreshCurrentUser?.();
        setSuccess(t('pages.profile.profileUpdatedShort'));
        setIsEditing(false);
        setTimeout(() => setSuccess(''), 4000);
        return;
      }

      setUser(updated);
      setFormData({
        department: updated.department || '',
        language: updated.language || 'en',
      });
      setSuccess(t('pages.profile.profileUpdated'));
      setIsEditing(false);
      setTimeout(() => setSuccess(''), 4000);

      await refreshCurrentUser?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('pages.profile.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        department: user.department || '',
        language: user.language || 'en',
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const initials = (user?.name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  if (loading) {
    return <ProfilePageSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('pages.profile.unableLoad')}</p>
          <button
            type="button"
            onClick={fetchUserProfile}
            className="mt-4 px-5 py-2.5 text-white rounded-xl font-medium transition-all hover:shadow-lg"
            style={{ backgroundColor: accent }}
          >
            {t('pages.profile.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-10">
      <PageFadeSection>
        <div
          className="relative overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-700/60 shadow-sm dark:shadow-none"
          style={{ background: `linear-gradient(165deg, ${accent}12 0%, transparent 42%), var(--tw-gradient-stops)` }}
        >
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/85 pointer-events-none" />
          <div className="relative p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 flex-1">
                <div className="relative flex-shrink-0">
                  <div
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
                  >
                    {initials}
                  </div>
                  <div
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 border-2 border-white dark:border-gray-900"
                    title={t('pages.profile.active')}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                      {user.name}
                    </h1>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getRoleBadgeStyle(user.role)}`}
                    >
                      <Shield className="w-3 h-3" />
                      {getRoleName(user.role)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </span>
                    {user.department && (
                      <span className="flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5" />
                        {departmentLabel(user.department) || user.department}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {t('pages.profile.joinedPrefix')}{' '}
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 lg:justify-end">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all flex items-center gap-2 text-sm font-medium"
                    >
                      <X className="w-4 h-4" /> {t('pages.profile.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2.5 rounded-xl text-white hover:opacity-95 transition-all flex items-center gap-2 text-sm font-medium disabled:opacity-60 shadow-md"
                      style={{ backgroundColor: accent }}
                    >
                      <Save className="w-4 h-4" /> {saving ? t('pages.profile.saving') : t('pages.profile.saveChanges')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2.5 rounded-xl text-white hover:opacity-95 transition-all flex items-center gap-2 text-sm font-medium shadow-md"
                    style={{ backgroundColor: accent }}
                  >
                    <Edit2 className="w-4 h-4" /> {t('pages.profile.editProfile')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageFadeSection>

      <PageFadeSection>
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200/80 dark:border-red-800/60 rounded-2xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/80 dark:border-emerald-800/60 rounded-2xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-800 dark:text-emerald-300">{success}</p>
          </div>
        )}

        {/* Single flowing panel (less boxy) */}
        <div className="rounded-3xl border border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm divide-y divide-gray-200/55 dark:divide-gray-700/55 shadow-sm overflow-hidden">
          {/* Personal */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-xl bg-amber-500/10 dark:bg-amber-400/10">
                <User className="w-5 h-5" style={{ color: accent }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.profile.personal')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('pages.profile.personalSub')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <InfoField
                icon={User}
                label={t('pages.profile.fullName')}
                value={user.name}
                editable={false}
                isEditing={isEditing}
                accent={accent}
                hint={t('pages.profile.nameHint')}
              />
              <InfoField
                icon={Mail}
                label={t('pages.profile.email')}
                value={user.email}
                editable={false}
                isEditing={isEditing}
                accent={accent}
              />
              <InfoField
                icon={Calendar}
                label={t('pages.profile.joined')}
                value={new Date(user.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                editable={false}
                isEditing={isEditing}
                accent={accent}
              />
            </div>
          </section>

          {/* Work */}
          <section className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-xl bg-amber-500/10 dark:bg-amber-400/10">
                <Briefcase className="w-5 h-5" style={{ color: accent }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.profile.work')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('pages.profile.workSub')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Building className="w-3.5 h-3.5 flex-shrink-0 opacity-80" style={{ color: accent }} />
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('pages.profile.department')}
                  </label>
                </div>
                {isEditing ? (
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 bg-white/80 dark:bg-gray-800/80 border border-gray-200/90 dark:border-gray-600/80 rounded-xl text-sm text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('pages.profile.selectDepartment')}</option>
                    {DEPARTMENT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(`pages.profile.dept_${o.value}`, { defaultValue: o.label })}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-relaxed min-h-[1.25rem]">
                    {departmentLabel(user.department) || t('pages.profile.notSpecified')}
                  </p>
                )}
              </div>
              <InfoField
                icon={Shield}
                label={t('pages.profile.role')}
                value={getRoleName(user.role)}
                editable={false}
                isEditing={isEditing}
                accent={accent}
              />
              <InfoField
                icon={Activity}
                label={t('pages.profile.sapEmployeeId')}
                value={user.sapEmployeeId || t('pages.profile.notAssigned')}
                editable={false}
                isEditing={isEditing}
                accent={accent}
              />
              <InfoField
                icon={Key}
                label={t('pages.profile.sapUuid')}
                value={user.sapEmployeeUUID || t('pages.profile.notSpecifiedUuid')}
                editable={false}
                isEditing={isEditing}
                accent={accent}
                hint={t('pages.profile.sapUuidHint')}
              />
            </div>
          </section>

          {/* Preferences + status — two columns on large screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-200/55 dark:lg:divide-gray-700/55">
            <section className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-amber-500/10 dark:bg-amber-400/10">
                  <Sparkles className="w-5 h-5" style={{ color: accent }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.profile.preferences')}</h2>
              </div>
              <div className="space-y-5">
                {isEditing ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5" style={{ color: accent }} />
                      <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        {t('pages.profile.language')}
                      </label>
                    </div>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 bg-white/80 dark:bg-gray-800/80 border border-gray-200/90 dark:border-gray-600/80 rounded-xl text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="en">{t('pages.profile.english')}</option>
                      <option value="de">{t('pages.profile.german')}</option>
                      <option value="zh">{t('pages.profile.chineseLang')}</option>
                    </select>
                    <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                      {t('pages.profile.moreLangHint')}
                    </p>
                  </div>
                ) : (
                  <InfoField
                    icon={Globe}
                    label={t('pages.profile.language')}
                    value={
                      user.language === 'de'
                        ? t('pages.profile.german')
                        : user.language === 'zh'
                          ? t('pages.profile.chineseLang')
                          : t('pages.profile.english')
                    }
                    editable={false}
                    isEditing={false}
                    accent={accent}
                    hint={t('pages.profile.moreLangHint')}
                  />
                )}
              </div>
            </section>

            <section className="p-6 sm:p-8 lg:pl-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-amber-500/10 dark:bg-amber-400/10">
                  <Shield className="w-5 h-5" style={{ color: accent }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.profile.accountStatus')}</h2>
              </div>
              <div className="rounded-2xl bg-gray-50/80 dark:bg-gray-800/30 px-4 py-1">
                <StatusRow label={t('pages.profile.account')}>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      user.isActive
                        ? 'bg-emerald-100 dark:bg-emerald-900/35 text-emerald-800 dark:text-emerald-300'
                        : 'bg-red-100 dark:bg-red-900/35 text-red-800 dark:text-red-300'
                    }`}
                  >
                    {user.isActive ? t('enums.active') : t('enums.inactive')}
                  </span>
                </StatusRow>
                <StatusRow label={t('pages.profile.email')}>
                  <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/35 text-emerald-800 dark:text-emerald-300 rounded-full text-xs font-semibold">
                    {t('pages.profile.verified')}
                  </span>
                </StatusRow>
                <StatusRow label={t('pages.profile.lastLogin')}>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : t('pages.profile.never')}
                  </span>
                </StatusRow>
                <StatusRow label={t('pages.profile.sapAccess')}>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      user.hasSapAccess
                        ? 'bg-emerald-100 dark:bg-emerald-900/35 text-emerald-800 dark:text-emerald-300'
                        : 'bg-amber-100 dark:bg-amber-900/35 text-amber-800 dark:text-amber-300'
                    }`}
                  >
                    {user.hasSapAccess ? t('pages.settings.enabled') : t('pages.settings.disabled')}
                  </span>
                </StatusRow>
              </div>
            </section>
          </div>
        </div>
      </PageFadeSection>
    </div>
  );
};

export default ProfilePage;
